import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

export function NewsletterForm(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const subscribe = useMutation({
    mutationFn: (value: string) =>
      api<{ ok: true }>('/newsletter', { method: 'POST', body: { email: value } }),
    onSuccess: () => {
      setNote({ kind: 'ok', text: "You're on the list." });
      setEmail('');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        setNote({ kind: 'err', text: 'Too many attempts — try again in a few minutes.' });
      } else if (err instanceof ApiError) {
        setNote({ kind: 'err', text: err.message });
      } else {
        setNote({ kind: 'err', text: 'Could not subscribe. Please try again.' });
      }
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setNote(null);
    const value = email.trim();
    if (!/\S+@\S+\.\S+/.test(value)) {
      setNote({ kind: 'err', text: 'Enter a valid email address.' });
      return;
    }
    subscribe.mutate(value);
  };

  return (
    <form className="newsletter-form-wrap" onSubmit={onSubmit} noValidate>
      <div className="newsletter-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          aria-label="Email address for the newsletter"
          autoComplete="email"
        />
        <button type="submit" className="btn btn-primary" disabled={subscribe.isPending}>
          {subscribe.isPending ? 'Joining…' : 'Join'}
        </button>
      </div>
      <div aria-live="polite">
        {note && <p className={`newsletter-note ${note.kind}`}>{note.text}</p>}
      </div>
    </form>
  );
}
